/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import type { Key } from 'react';

import { Autocomplete, Chip, ChipGroup, Grid, IconButton, Typography2 } from '@coursera/cds-core';
import { AddIcon } from '@coursera/cds-icons';

import useSkillsEditor, { CREATE_USER_GENERATED_KEY } from 'bundles/personalization-settings/hooks/useSkillsEditor';
import type { AvailableSkill } from 'bundles/personalization-settings/hooks/useSkillsEditor';
import { removeSkill } from 'bundles/personalization-settings/utils/skillsWantedV2';
import type { SelectedSkill } from 'bundles/personalization-settings/utils/skillsWantedV2';

import _t from 'i18n!nls/personalization-settings';

const styles = {
  // CDS Grid `spacing` only applies between `Grid item` children, so it leaves
  // the chips flush against the input here. Add explicit breathing room between
  // the selected-skills chips and the "Add a skill" field instead.
  chips: css`
    margin-bottom: var(--cds-spacing-100);
  `,
};

export type SkillsEditorProps = {
  /** The learner's currently selected desired skills (controlled). */
  value: SelectedSkill[];
  /** Called with the next selection whenever a skill is added or removed. */
  onChange: (skills: SelectedSkill[]) => void;
  /** Catalog skills the learner can pick from, supplied by the parent container. */
  availableSkills?: readonly AvailableSkill[];
  /**
   * Called with the current typeahead text as the learner types. The container
   * uses it to load matching catalog skills into `availableSkills`.
   */
  onInputChange?: (input: string) => void;
  /** True while the container is loading `availableSkills`, so the popover can show a loading state. */
  loading?: boolean;
};

const chipKey = (skill: SelectedSkill) => (skill.type === 'catalog' ? `catalog:${skill.id}` : `user:${skill.name}`);

/**
 * The "Desired skills" editor for the Personalization settings page: a chip
 * group of the learner's selected skills plus a typeahead to add more. The
 * catalog of selectable skills is supplied by the parent via `availableSkills`;
 * a learner can also add a free-text (user-generated) skill that isn't in it.
 *
 * Controlled via `value`/`onChange`. It does not read or persist the Learner
 * Object itself — the page wires the loaded `skills_wanted_v2` value in (LO-4743)
 * and fires the update mutation on Save (LO-4744).
 */
const SkillsEditor = ({ value, onChange, availableSkills = [], onInputChange, loading }: SkillsEditorProps) => {
  const [input, setInput] = React.useState('');
  const inputRef = React.useRef<HTMLInputElement>(null);

  // The CDS Autocomplete retains the last-selected option key as internal state.
  // When a chip is removed, that option reappears in the list still visually
  // "selected" until the dropdown is closed and reopened. Remounting (via `key`)
  // on chip removal resets the internal state cleanly.
  const autocompleteResetKey = React.useRef(0);
  const prevValueRef = React.useRef(value);
  if (value !== prevValueRef.current) {
    if (value.length < prevValueRef.current.length) autocompleteResetKey.current += 1;
    prevValueRef.current = value;
  }

  const { options, trimmedInput, addSkillFromOption, addSkillFromInput } = useSkillsEditor({
    value,
    onChange,
    availableSkills,
    input,
  });

  // Update the controlled input and notify the container so it can load matching
  // catalog skills for the typeahead.
  const updateInput = (next: string) => {
    setInput(next);
    onInputChange?.(next);
  };

  const handleSelectionChange = (key: Key | null) => {
    if (key == null) return;
    addSkillFromOption(String(key));
    updateInput('');
    // Blur so the next click re-triggers focus and reopens the dropdown.
    // (popoverTrigger="focus" only fires on focus events, not on click when already focused.)
    inputRef.current?.blur();
  };

  // Enter adds whatever is typed — but only when the learner hasn't arrow-keyed
  // to a specific option. If an option is highlighted, `aria-activedescendant`
  // is set and Autocomplete's own `onSelectionChange` commits that option, so we
  // must not also add the raw text (which would wrongly create a user-generated
  // skill instead of adding the highlighted catalog one).
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter' || !trimmedInput) return;
    if (event.currentTarget.getAttribute('aria-activedescendant')) return;
    event.preventDefault();
    addSkillFromInput();
    updateInput('');
    inputRef.current?.blur();
  };

  const handleAddClick = () => {
    addSkillFromInput();
    updateInput('');
  };

  return (
    <Grid container direction="column" spacing={8}>
      {value.length > 0 && (
        <div css={styles.chips}>
          <ChipGroup label={_t('Selected skills')} variant="delete">
            {value.map((skill) => (
              <Chip key={chipKey(skill)} onClick={() => onChange(removeSkill(value, skill))}>
                {skill.name}
              </Chip>
            ))}
          </ChipGroup>
        </div>
      )}
      <Autocomplete
        key={autocompleteResetKey.current}
        ref={inputRef}
        fullWidth
        aria-label={_t('Add a skill')}
        placeholder={_t('Add a skill')}
        value={input}
        onInputChange={updateInput}
        onSelectionChange={handleSelectionChange}
        inputProps={{ onKeyDown: handleKeyDown }}
        popoverTrigger="focus"
        loading={loading}
        allowsCustomValue
        renderNotFoundMessage={() => (
          <Typography2 component="div" variant="bodySecondary" color="supportText">
            {_t('No skills found')}
          </Typography2>
        )}
        items={options}
        inputActionButtons={
          <IconButton
            icon={<AddIcon />}
            aria-label={_t('Add skill')}
            tooltip={_t('Add skill')}
            size="small"
            disabled={!trimmedInput}
            onClick={handleAddClick}
          />
        }
      >
        {(option) => (
          <Autocomplete.Option key={option.key} textValue={option.label}>
            {option.key === CREATE_USER_GENERATED_KEY ? _t('Add “#{label}”', { label: option.label }) : option.label}
          </Autocomplete.Option>
        )}
      </Autocomplete>
    </Grid>
  );
};

export default SkillsEditor;
